import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { SwapHoriz } from '@mui/icons-material';
import { Box, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { getAppStoreById } from '@/api/appstore.api';
import { useAppStoreStore } from '@/stores/appstore.store';
import { useFavoriteStore } from '@/stores/favoriteStore.store';
import { useOrganizationTeamStore } from '@/stores/organizationTeam.store';
import SearchModal from '@/components/modal/search/SearchModal';
import useCheckIsMobileSize from '@/hooks/useCheckIsMobileSize';

const MENU_ITEM_HEIGHT = 54;
const DESKTOP_LIST_MAX_HEIGHT = 548;
const MOBILE_VISIBLE_ITEM_COUNT = 5;
const MOBILE_LIST_MAX_HEIGHT = MENU_ITEM_HEIGHT * MOBILE_VISIBLE_ITEM_COUNT;

interface HospitalMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  redirectToReviews?: boolean;
}

const menuPaperSx = {
  width: 220,
  maxWidth: 220,
  pb: '16px',
  bgcolor: 'white',
  borderRadius: '4px',
  overflowX: 'hidden',
  overflowY: 'visible',
  maxHeight: 'none',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.10), 0px 1px 3px rgba(0, 0, 0, 0.25)'
};

const sectionHeaderSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  height: 44,
  pt: '14px',
  pb: '8px',
  px: '12px',
  bgcolor: 'white',
  borderBottom: '1px solid #F3F4F6',
  overflow: 'hidden',
  opacity: 1,
  gap: '4px',
  '&.Mui-disabled': {
    opacity: 1
  },
  '& .MuiListItemIcon-root': {
    minWidth: 16,
    width: 16,
    mr: 0
  },
  '& .MuiListItemText-root': {
    m: 0
  },
  '& .MuiListItemText-primary': {
    color: '#6B00D7',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '17.5px'
  }
};

const favoriteSectionHeaderSx = {
  ...sectionHeaderSx,
  pointerEvents: 'none',
  flexShrink: 0,
  '& .MuiListItemText-root': {
    m: 0,
    flex: '1 1 0'
  },
  '&:hover': {
    bgcolor: 'white'
  }
};

const favoriteSectionSx = {
  flexShrink: 0,
  overflow: 'visible'
};

const favoriteChangeButtonSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  border: 'none',
  bgcolor: 'transparent',
  cursor: 'pointer',
  p: 0,
  pointerEvents: 'auto',
  color: '#1F2937',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '15px',
  fontFamily: 'inherit',
  '& .MuiSvgIcon-root': {
    fontSize: 14,
    color: '#1F2937'
  }
};

const badgeIconWrapperSx = {
  minWidth: 16,
  width: 16,
  height: 16,
  mr: 0,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const HospitalBadgeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="8" fill="#6B00D7" />
    <rect x="7" y="4" width="2" height="8" fill="white" />
    <rect x="4" y="7" width="8" height="2" fill="white" />
  </svg>
);

const favoriteHeartPath = [
  'M8 12.35C7.82 12.17 4.25 9.4 4.25 7.05C4.25 5.73 5.28 4.75 6.55 4.75',
  'C7.28 4.75 7.86 5.14 8.1 5.68C8.34 5.14 8.92 4.75 9.65 4.75C10.92 4.75 11.95 5.73',
  '11.95 7.05C11.95 9.4 8.38 12.17 8.2 12.35H8Z'
].join('');

const FavoriteBadgeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="8" fill="#6B00D7" />
    <path d={favoriteHeartPath} fill="white" />
  </svg>
);

const storeItemTextSlotProps = {
  primary: {
    sx: {
      color: '#1F2937',
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '17.5px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  },
  secondary: {
    sx: {
      color: '#4B5563',
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '15px',
      mt: '2px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  }
};

const storeItemSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  minHeight: 54,
  height: 54,
  px: '16px',
  py: '12px',
  overflow: 'hidden',
  whiteSpace: 'normal',
  borderBottom: '1px solid #F9FAFB',
  '& .MuiListItemText-root': {
    m: 0,
    minWidth: 0,
    overflow: 'hidden'
  }
};

const getStoreListScrollSx = (isMobile: boolean, teamCount: number) => {
  const listMaxHeight = isMobile ? MOBILE_LIST_MAX_HEIGHT : DESKTOP_LIST_MAX_HEIGHT;
  const showScrollbar = isMobile ? teamCount > MOBILE_VISIBLE_ITEM_COUNT : teamCount > 7;

  return {
    width: '100%',
    maxWidth: '100%',
    maxHeight: listMaxHeight,
    flexShrink: 0,
    overflowY: showScrollbar ? 'auto' : 'hidden',
    overflowX: 'hidden',
    boxSizing: 'border-box',
    pb: '4px',
    WebkitOverflowScrolling: 'touch',
    ...(showScrollbar && {
      scrollbarGutter: 'stable',
      '&::-webkit-scrollbar': {
        width: 4
      },
      '&::-webkit-scrollbar-thumb': {
        bgcolor: '#4B5563',
        borderRadius: '24px'
      },
      '&::-webkit-scrollbar-track': {
        bgcolor: 'transparent'
      }
    })
  };
};

const getMenuPaperSx = (isMobile: boolean) => ({
  ...menuPaperSx,
  ...(isMobile && {
    maxHeight: 'calc(100dvh - 24px)',
    overflowY: 'auto'
  })
});

const getMenuListSx = (isMobile: boolean) => ({
  py: 0,
  width: 220,
  maxWidth: 220,
  maxHeight: isMobile ? 'calc(100dvh - 24px)' : 'none',
  overflowX: 'hidden',
  overflowY: isMobile ? 'auto' : 'visible'
});

const dividerSx = {
  height: 4,
  bgcolor: '#F3F4F6',
  border: 'none',
  my: 0
};

const actionItemSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  overflow: 'hidden',
  px: '12px',
  py: '10px',
  '& .MuiListItemIcon-root': {
    minWidth: 'auto',
    mr: '4px'
  },
  '& .MuiListItemText-primary': {
    color: '#1F2937',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '15px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  '& .MuiSvgIcon-root': {
    fontSize: 14,
    color: '#1F2937'
  }
};

const HospitalMenu = ({ anchorEl, onClose, redirectToReviews = false }: HospitalMenuProps) => {
  const router = useRouter();
  const isMobile = useCheckIsMobileSize();
  const [showSearchModal, setShowSearchModal] = useState(false);
  const { appUserStoreInfo } = useFavoriteStore();
  const { teams } = useOrganizationTeamStore();
  const { setAppStores } = useAppStoreStore();

  const teamList = Object.values(teams);
  const hasTeams = teamList.length > 0;
  const hasFavoriteStore = !!appUserStoreInfo?.storeId;

  const handleStoreClick = async (storeId: string) => {
    const res = await getAppStoreById(Number(storeId));
    if (res) {
      setAppStores([res], Number(storeId));
      onClose();

      setTimeout(() => {
        if (redirectToReviews) {
          router.push(`/stores/${storeId}/reviews`);
        } else {
          router.push(`/stores/${storeId}/dashboard`);
        }
      }, 100);
    } else {
      onClose();
    }
  };

  const handleStoreSettingClick = () => {
    setShowSearchModal(true);
    onClose();
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={onClose}
        slotProps={{
          list: {
            sx: getMenuListSx(isMobile)
          }
        }}
        sx={{
          mt: 1.5,
          '& .MuiPaper-root': getMenuPaperSx(isMobile)
        }}
      >
        <MenuItem disabled sx={sectionHeaderSx}>
          <ListItemIcon sx={badgeIconWrapperSx}>
            <HospitalBadgeIcon />
          </ListItemIcon>
          <ListItemText>내 병원</ListItemText>
        </MenuItem>
        {hasTeams ? (
          <Box sx={getStoreListScrollSx(isMobile, teamList.length)} onWheel={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}>
            {teamList.map((team, index) => (
              <MenuItem key={team.teamId || index} onClick={() => handleStoreClick(team.storeId.toString())} sx={storeItemSx}>
                <ListItemText
                  primary={team.storeName}
                  secondary={team.storeDetail || undefined}
                  slotProps={{
                    ...storeItemTextSlotProps,
                    secondary: {
                      ...storeItemTextSlotProps.secondary,
                      sx: {
                        ...storeItemTextSlotProps.secondary.sx,
                        display: team.storeDetail ? 'block' : 'none'
                      }
                    }
                  }}
                />
              </MenuItem>
            ))}
          </Box>
        ) : (
          <MenuItem
            disabled
            sx={{
              ...storeItemSx,
              opacity: 1,
              '&.Mui-disabled': { opacity: 1 },
              '& .MuiListItemText-primary': {
                color: '#4B5563',
                fontSize: 12,
                fontWeight: 400,
                lineHeight: '15px'
              }
            }}
          >
            <ListItemText>등록된 병원이 없습니다</ListItemText>
          </MenuItem>
        )}
        <Divider sx={dividerSx} />

        {hasFavoriteStore && (
          <Box sx={favoriteSectionSx}>
            <MenuItem disableRipple sx={favoriteSectionHeaderSx}>
              <ListItemIcon sx={badgeIconWrapperSx}>
                <FavoriteBadgeIcon />
              </ListItemIcon>
              <ListItemText>관심 병원</ListItemText>
              <Box component="button" type="button" onClick={handleStoreSettingClick} sx={favoriteChangeButtonSx}>
                <SwapHoriz fontSize="small" />
                변경
              </Box>
            </MenuItem>
            <MenuItem
              onClick={() => handleStoreClick(appUserStoreInfo.storeId.toString())}
              sx={{
                ...storeItemSx,
                borderBottom: 'none'
              }}
            >
              <ListItemText
                primary={appUserStoreInfo.storeName}
                secondary={appUserStoreInfo.storeDetail || undefined}
                slotProps={{
                  ...storeItemTextSlotProps,
                  secondary: {
                    ...storeItemTextSlotProps.secondary,
                    sx: {
                      ...storeItemTextSlotProps.secondary.sx,
                      display: appUserStoreInfo.storeDetail ? 'block' : 'none'
                    }
                  }
                }}
              />
            </MenuItem>
          </Box>
        )}

        {!hasFavoriteStore && (
          <MenuItem onClick={handleStoreSettingClick} sx={actionItemSx}>
            <ListItemIcon>
              <SwapHoriz fontSize="small" />
            </ListItemIcon>
            <ListItemText>관심 병원 등록하기</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {showSearchModal && <SearchModal showModal={showSearchModal} handleShowModal={() => setShowSearchModal(false)} type="CHANGE" />}
    </>
  );
};

export default HospitalMenu;
